import { PERSPECTIVE_BUCKET } from './domain.js';
import { countsInAggregates, countsInHeadcount, fxFactor } from './cost.js';
import { bucketMetaFor, currentBucket, stageIncludesId, strandedPlanOf, waveOf } from './decisions.js';
import { getActiveLevels, getEmpField, getHeadFor, ORG_HEAD_GROUP, passesFilters, resolveField, } from './fields.js';
export const ROOT_ID = '__root__';
export function emptyComposition() {
    return {
        Remain: 0, RemainBu: 0, 'Carve-out': 0, CarveBu: 0,
        'No longer required': 0, Open: 0, Inactive: 0,
    };
}
/**
 * Content-addressed group id — 'G:lvl1=Ops||lvl2=Field'.
 *
 * Deriving identity from the path is why renaming a group changes the identity of everything keyed
 * to it (heads, per-scenario overrides). Single-user the maps are rewritten in the same tick;
 * concurrently it is a hazard, which is why renameGroup must be one atomic server command.
 */
export function groupNodeId(path) {
    return 'G:' + path.map((p) => `${p.levelKey}=${p.value}`).join('||');
}
/**
 * The chart node id for a person.
 *
 * Chart node ids and employee ids are different namespaces — a person and a group can occupy the same
 * tree, and a group id is content-addressed on its level path — so the prefix keeps them apart. It is
 * exported because the id scheme belongs to the engine: clients that map a chart selection back to a
 * person, and tests that address a card, must not each carry their own copy of the prefix.
 */
export function personNodeId(employeeId) {
    return `E:${employeeId}`;
}
/** How a group id's levels read when written out for a person. */
const PATH_SEPARATOR = ' \u203a ';
/**
 * A human label for a chart node id — what to call the thing somebody has open.
 *
 * FROM THE ID ITSELF, which is what makes it usable across two people's screens. A group id is
 * content-addressed on its display level path, and that path is chosen by each reader's own lens
 * (perspective, custom pivot, target view) and never broadcast — so a colleague under a different lens
 * announces an id that does not exist in your tree at all. Every consumer of a peer's selection until now
 * was an exact-id join against the drawn nodes, which in that case simply misses: no marker, no counter,
 * and a roster line that could only say "viewing a node".
 *
 * Decoding the id sidesteps that entirely. `G:lvl1=Ops||lvl2=Field` carries its own labels, so "Ops › Field"
 * is answerable whether or not that group is in your tree, drawn, filtered in, or expanded — which are
 * four separate ways the marker on a card goes silent.
 *
 * `nameOf` is asked only for a person, whose coordinate IS lens-stable (`E:<employeeId>`); the caller
 * passes a lookup over the unfiltered roster so that a colleague reading somebody your filters hide is
 * still named. Returns null when the id names nothing this can speak for — the root, or a person the
 * roster does not have — so the caller decides what to say rather than being handed a guess.
 */
export function describeNodeId(nodeId, nameOf) {
    if (nodeId.startsWith('E:'))
        return nameOf(nodeId.slice(2)) ?? null;
    if (!nodeId.startsWith('G:'))
        return null;
    const labels = nodeId
        .slice('G:'.length)
        .split('||')
        .map((segment) => {
        // The FIRST `=` only: a level VALUE may contain one — "Ops=West" is an ordinary spreadsheet cell.
        const eq = segment.indexOf('=');
        return eq === -1 ? segment : segment.slice(eq + 1);
    })
        .filter((label) => label.length > 0);
    return labels.length ? labels.join(PATH_SEPARATOR) : null;
}
/** The employee a chart node id refers to, or null when the node is not a person. */
export function employeeIdFromNode(nodeId) {
    return nodeId.startsWith('E:') ? nodeId.slice(2) : null;
}
export function freshLens() {
    return {
        perspective: 'as-is',
        stage: 'day1',
        viewMode: 'hide',
        viewLayout: 'grouping',
        viewGrouping: null,
        filters: [],
        targetView: false,
        wantStageCounts: false,
    };
}
export function isCustomPivot(lens) {
    return !!(lens.viewGrouping && lens.viewGrouping.length);
}
/** Out of scope for the current perspective, but shown dimmed rather than removed. */
export function isDimmed(state, lens, personId) {
    if (lens.perspective === 'as-is')
        return false;
    if (lens.viewMode !== 'grey')
        return false;
    const bucket = currentBucket(state.design, personId);
    return (bucket !== PERSPECTIVE_BUCKET[lens.perspective] ||
        !stagePassesLens(state.design, personId, lens));
}
export function isVisible(state, lens, personId) {
    if (lens.perspective === 'as-is')
        return true;
    if (lens.viewMode === 'grey')
        return true;
    return (currentBucket(state.design, personId) === PERSPECTIVE_BUCKET[lens.perspective] &&
        stagePassesLens(state.design, personId, lens));
}
function stagePassesLens(design, personId, lens) {
    if (lens.perspective !== 'carve-out')
        return true;
    return stageIncludesId(design, personId, lens.stage);
}
/** The levels the chart groups by: target design, custom pivot, or the canonical hierarchy. */
export function getDisplayLevels(state, lens) {
    if (lens.targetView)
        return (state.targetLevels ?? []).map((l) => ({ key: l.key, label: l.label }));
    if (isCustomPivot(lens)) {
        return lens.viewGrouping.map((l) => ({ key: l.fieldId, label: l.label }));
    }
    return getActiveLevels(state, lens.perspective);
}
/* ------------------------------------------------------------------ *
 * buildHierarchy
 * ------------------------------------------------------------------ */
export function buildHierarchy(state, lens = freshLens()) {
    const nodes = [];
    const design = state.design;
    const all = [...state.employees, ...design.buildUps];
    const filterCtx = state;
    const visible = all.filter((p) => isVisible(state, lens, p.id) && passesFilters(filterCtx, p, lens.perspective, lens.filters));
    const root = {
        id: ROOT_ID,
        parentId: '',
        kind: 'root',
        label: 'Organisation',
        _expanded: true,
        data: { head: null, stageCounts: null },
    };
    nodes.push(root);
    if (lens.viewLayout === 'reporting') {
        return buildReportingHierarchy(state, lens, nodes, all, visible);
    }
    const levels = getDisplayLevels(state, lens);
    const pivot = isCustomPivot(lens);
    const targetActive = lens.targetView;
    // Under the target view a person's unit comes from their target placement, not their real org.
    const levelValue = (p, key) => targetActive
        ? getEmpField(p, key, 'target', state.targetOverrides)
        : resolveField(filterCtx, p, key, lens.perspective);
    const stageCounts = lens.wantStageCounts
        ? computeStageCountsByGroup(state, lens, all, levels, levelValue, pivot)
        : null;
    const bucketReader = (id) => currentBucket(design, id);
    // Head of the whole organisation: shown on the root card, never as a leaf. Heads are a
    // canonical-structure concept, so a pivot or the target view has none.
    const orgHeadId = pivot || targetActive ? null : getHeadFor(state, ORG_HEAD_GROUP, lens.perspective, bucketReader);
    let orgHead = orgHeadId ? all.find((p) => p.id === orgHeadId) ?? null : null;
    if (orgHead &&
        (!isVisible(state, lens, orgHead.id) ||
            !passesFilters(filterCtx, orgHead, lens.perspective, lens.filters))) {
        orgHead = null;
    }
    root.data = { head: orgHead, stageCounts: stageCounts ? stageCounts.get(ROOT_ID) ?? null : null };
    if (!levels.length) {
        for (const p of visible) {
            if (orgHeadId && p.id === orgHeadId)
                continue;
            nodes.push({ id: personNodeId(p.id), parentId: ROOT_ID, kind: 'person', label: p.name, empId: p.id, data: p });
        }
        return nodes;
    }
    // Default expansion reaches the second group level.
    const defaultExpandDepth = Math.min(1, levels.length - 1);
    const recurse = (people, parentId, levelIdx, path) => {
        // Someone who heads an ancestor group never also appears as a leaf beneath it.
        const ancestorHeadIds = pivot || targetActive
            ? new Set()
            : new Set([orgHeadId]
                .concat(path.map((_, i) => getHeadFor(state, groupNodeId(path.slice(0, i + 1)), lens.perspective, bucketReader)))
                .filter((x) => !!x));
        if (levelIdx >= levels.length) {
            for (const p of people) {
                if (ancestorHeadIds.has(p.id))
                    continue;
                nodes.push({ id: personNodeId(p.id), parentId, kind: 'person', label: p.name, empId: p.id, data: p });
            }
            return;
        }
        const level = levels[levelIdx];
        const groups = new Map();
        // Ragged hierarchy: a blank at this level puts the person directly under the parent group.
        const direct = [];
        for (const p of people) {
            const v = levelValue(p, level.key);
            const blank = v == null || String(v).trim() === '';
            // A custom pivot keeps the analytic "(No X)" bucket; the canonical structure is ragged.
            if (blank && !pivot) {
                direct.push(p);
                continue;
            }
            const key = blank ? `(No ${level.label})` : String(v);
            const bucketList = groups.get(key);
            if (bucketList)
                bucketList.push(p);
            else
                groups.set(key, [p]);
        }
        for (const p of direct) {
            if (ancestorHeadIds.has(p.id))
                continue;
            nodes.push({ id: personNodeId(p.id), parentId, kind: 'person', label: p.name, empId: p.id, data: p });
        }
        const sorted = [...groups.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0])));
        for (const [groupName, groupPeople] of sorted) {
            const newPath = [
                ...path,
                { levelKey: level.key, label: level.label, value: groupName },
            ];
            const id = groupNodeId(newPath);
            let head = null;
            if (!pivot && !targetActive) {
                const headId = getHeadFor(state, id, lens.perspective, bucketReader);
                head = headId ? all.find((p) => p.id === headId) ?? null : null;
                // A head only leads the group where they are themselves in scope, and must pass the active
                // filters — otherwise a filtered-out head keeps an otherwise-empty group alive.
                if (head &&
                    (!isVisible(state, lens, head.id) ||
                        !passesFilters(filterCtx, head, lens.perspective, lens.filters))) {
                    head = null;
                }
            }
            // Open positions and inactive people are excluded from filled headcount, FTE and cost and get
            // their own segments. Attrition leavers stay visible as cards but count nowhere, so a group's
            // headline agrees with its own stage line and with the phase bridge.
            const real = groupPeople.filter((p) => !p.isLeaver && countsInHeadcount(p));
            /*
              The same population under HIDE semantics, whatever the view mode is.
      
              `isVisible` short-circuits to true under grey, which is what puts the out-of-scope people in
              this group in the first place; asking the perspective-and-stage question directly is the only
              way to say how many of them the card is actually about. Under hide this is `real.length` by
              construction, so the card can compare the two without knowing which mode it is in.
            */
            const inScope = real.filter((p) => lens.perspective === 'as-is' ||
                (currentBucket(design, p.id) === PERSPECTIVE_BUCKET[lens.perspective] &&
                    stagePassesLens(design, p.id, lens))).length;
            const composition = emptyComposition();
            for (const p of groupPeople) {
                if (p.isLeaver)
                    continue;
                if (p.openPosition) {
                    composition.Open++;
                    continue;
                }
                if (p.inactive) {
                    composition.Inactive++;
                    continue;
                }
                const bucket = currentBucket(design, p.id);
                if (p.isBuildUp) {
                    if (bucket === 'Carve-out')
                        composition.CarveBu++;
                    else
                        composition.RemainBu++;
                }
                else {
                    composition[bucket] = (composition[bucket] ?? 0) + 1;
                }
            }
            nodes.push({
                id,
                parentId,
                kind: 'group',
                label: groupName,
                _expanded: levelIdx === defaultExpandDepth,
                data: {
                    name: groupName,
                    levelKey: level.key,
                    levelLabel: level.label,
                    levelIdx,
                    depth: levelIdx + 1,
                    path: newPath,
                    count: real.length,
                    inScope,
                    open: groupPeople.filter((p) => !p.isLeaver && p.openPosition).length,
                    inactive: groupPeople.filter((p) => !p.isLeaver && p.inactive).length,
                    salary: real.reduce((a, p) => a +
                        (Number(getEmpField(p, 'salary', lens.perspective, design.scenarioOverrides)) || 0) *
                            fxFactor(p, state.assumptions), 0),
                    fte: real.reduce((a, p) => a + (Number(getEmpField(p, 'fte', lens.perspective, design.scenarioOverrides)) || 0), 0),
                    head,
                    composition,
                    stageCounts: stageCounts ? stageCounts.get(id) ?? null : null,
                },
            });
            recurse(groupPeople, id, levelIdx + 1, newPath);
        }
    };
    recurse(targetActive ? visible.filter((p) => isPlacedInTarget(state, p)) : visible, ROOT_ID, 0, []);
    addTargetDeclaredUnits(state, lens, nodes);
    return pruneEmptyGroups(nodes);
}
/** A person is in the target org once they carry a top-level target-unit placement. */
export function isPlacedInTarget(state, person) {
    const first = (state.targetLevels ?? [])[0];
    if (!first)
        return false;
    const v = getEmpField(person, first.key, 'target', state.targetOverrides);
    return v != null && String(v).trim() !== '';
}
/**
 * Ensure every declared target unit exists as a group node even with nobody placed into it — the
 * blueprint has to be visible and navigable before it is populated.
 */
function addTargetDeclaredUnits(state, lens, nodes) {
    if (!lens.targetView || !(state.targetUnits ?? []).length)
        return;
    const levels = state.targetLevels ?? [];
    if (!levels.length)
        return;
    const have = new Set(nodes.filter((n) => n.kind === 'group').map((n) => n.id));
    for (const unitPath of state.targetUnits) {
        for (let k = 0; k < Math.min(unitPath.length, levels.length); k++) {
            const slice = [];
            for (let i = 0; i <= k; i++) {
                slice.push({ levelKey: levels[i].key, label: levels[i].label, value: unitPath[i] });
            }
            const id = groupNodeId(slice);
            if (have.has(id))
                continue;
            have.add(id);
            nodes.push({
                id,
                parentId: k === 0 ? ROOT_ID : groupNodeId(slice.slice(0, k)),
                kind: 'group',
                label: unitPath[k],
                _expanded: k === 0,
                data: {
                    name: unitPath[k],
                    levelKey: levels[k].key,
                    levelLabel: levels[k].label,
                    levelIdx: k,
                    depth: k + 1,
                    path: slice,
                    count: 0,
                    // A declared-empty unit is in scope by definition: nobody is out of it.
                    inScope: 0,
                    open: 0,
                    inactive: 0,
                    salary: 0,
                    fte: 0,
                    head: null,
                    composition: emptyComposition(),
                    stageCounts: null,
                    declared: true,
                },
            });
        }
    }
}
/**
 * Drop groups with nothing under them, repeatedly — removing a leaf group can empty its parent.
 *
 * Two kinds of empty group survive: one with a designated head (the head IS the group's content, and
 * a manager whose whole team moved elsewhere still needs to be visible), and a declared target unit
 * (an empty box is the point of a blueprint).
 */
export function pruneEmptyGroups(nodes) {
    let remaining = nodes;
    for (;;) {
        const byParent = new Map();
        for (const n of remaining) {
            if (!n.parentId)
                continue;
            byParent.set(n.parentId, (byParent.get(n.parentId) ?? 0) + 1);
        }
        const next = remaining.filter((n) => {
            if (n.kind !== 'group')
                return true;
            if (n.data.declared)
                return true;
            if (n.data.head)
                return true;
            return (byParent.get(n.id) ?? 0) > 0;
        });
        if (next.length === remaining.length)
            return next;
        remaining = next;
    }
}
/* ------------------------------------------------------------------ *
 * Stage comparison line
 * ------------------------------------------------------------------ */
function freshStageCounts() {
    const zero = () => ({ hc: 0, fte: 0, cost: 0 });
    return { asis: zero(), p: zero(), d1: zero(), d2: zero(), r1: zero(), rs: zero() };
}
/**
 * HC / FTE / cost per group at every stage, computed for ALL stages regardless of the active lens —
 * that side-by-side comparison is the whole point of the line — but respecting active filters.
 *
 * Group keys mirror the recursion below exactly (same resolved values, same ragged and pivot blank
 * rules), so a group's stage line rolls up precisely the descendants shown under it.
 */
function computeStageCountsByGroup(state, lens, all, levels, levelValue, pivot) {
    const design = state.design;
    const out = new Map();
    const bump = (key, stages, fte, cost) => {
        let c = out.get(key);
        if (!c) {
            c = freshStageCounts();
            out.set(key, c);
        }
        for (const s of stages) {
            c[s].hc++;
            c[s].fte += fte;
            c[s].cost += cost;
        }
    };
    for (const p of all) {
        if (p.isLeaver || !countsInHeadcount(p))
            continue;
        if (!passesFilters(state, p, lens.perspective, lens.filters))
            continue;
        const stages = [];
        if (!p.isBuildUp)
            stages.push('asis');
        const bucket = currentBucket(design, p.id);
        if (bucket === 'Carve-out') {
            if (stageIncludesId(design, p.id, 'perimeter'))
                stages.push('p');
            if (stageIncludesId(design, p.id, 'day1'))
                stages.push('d1');
            if (stageIncludesId(design, p.id, 'day2'))
                stages.push('d2');
        }
        else if (!p.isBuildUp && bucket !== 'No longer required') {
            // RemainCo columns: existing people staying behind, matching the remain-mirror bridge.
            // RS is the steady state after the stranded plan's rightsizings have left.
            stages.push('r1');
            const plan = strandedPlanOf(design, p);
            if (!(plan && plan.action === 'rightsize'))
                stages.push('rs');
        }
        if (!stages.length)
            continue;
        const fte = Number(p.fte) || 0;
        const cost = (Number(p.salary) || 0) * fxFactor(p, state.assumptions);
        bump(ROOT_ID, stages, fte, cost);
        const path = [];
        for (const level of levels) {
            const v = levelValue(p, level.key);
            const blank = v == null || String(v).trim() === '';
            if (blank && !pivot)
                break; // ragged: the person hangs here, deeper groups do not exist
            path.push({
                levelKey: level.key,
                label: level.label,
                value: blank ? `(No ${level.label})` : String(v),
            });
            bump(groupNodeId(path), stages, fte, cost);
        }
    }
    return out;
}
/* ------------------------------------------------------------------ *
 * Reporting-line layout
 * ------------------------------------------------------------------ */
/**
 * Each person's parent is their supervisor, resolved scenario-aware so per-scenario reassignment
 * works. When a supervisor is not in the visible set, climb to the nearest visible manager; a person
 * with no reachable manager hangs off the root.
 *
 * Two cycle guards are needed, not one: the climb can loop, and two mutually-supervising VISIBLE
 * people would each point at the other and produce a graph the chart cannot lay out.
 */
export function buildReportingHierarchy(state, lens, nodes, all, visible) {
    const design = state.design;
    const byId = new Map(all.map((p) => [String(p.id), p]));
    const visibleIds = new Set(visible.map((p) => String(p.id)));
    const supervisorOf = (p) => {
        const s = String(getEmpField(p, 'supervisorId', lens.perspective, design.scenarioOverrides) ?? '').trim();
        return s && s !== String(p.id) ? s : ''; // self-supervision reads as top-of-chain
    };
    /*
      What the layout had to invent, counted where the climb already knows it.
  
      Three different reasons a person ends up somewhere other than under their own manager, and they are
      worth telling apart: an id that resolves to nobody is a data problem, a manager the LENS removed is
      a consequence of how you are looking, and a cycle is neither. All three used to be silent, and the
      lens case is the dangerous one — narrow to one country with the reporting line on and that country's
      managers' reports redraw as though they reported to the board.
    */
    const integrity = { orphans: 0, spliced: 0, withheld: 0, cycles: 0 };
    /*
      The visible people whose manager was removed by the ACCESS SCOPE rather than by the lens.
  
      Carried on the payload (`SeparationState.scope`) rather than derived here, because only the server
      knows which of the two removed somebody: by the time this runs, a manager withheld by a permission
      and a manager who never existed look identical.
    */
    const withheldSupervisor = new Set(state.scope?.withheldSupervisorOf ?? []);
    /*
      Exactly one event per person, in exactly one category — which is what makes the counts readable.
  
      The question is only ever about the FIRST hop: either this person is drawn under their own manager,
      or they are not. If they are not, the reason is one of two things, and they are worth telling apart:
      the id resolves to nobody at all (a data problem, in the file), or the manager exists and the lens
      or a filter removed them (a consequence of how you are looking, and the dangerous one — narrow to
      one country and that country's managers' reports redraw as though they reported to the board).
  
      Where the climb lands after that is a layout detail, not a second event. Counting it as one is how
      an earlier version of this double-counted every splice.
    */
    const parentNodeId = (p) => {
        const first = supervisorOf(p);
        if (!first)
            return ROOT_ID; // top of chain: nothing was inferred
        if (visibleIds.has(first))
            return personNodeId(first); // drawn exactly as the data says
        /*
          DO NOT SPLICE ACROSS A PERMISSION BOUNDARY, and it is a decision as much as a fix.
    
          The climb is right for a FILTER and wrong for a permission, and the difference is
          diagnosability: a reader can clear a filter and see the real line, and cannot clear a
          permission — so the fabricated manager is unfalsifiable to them. Worse, the climb LEAKS.
          Landing under a distant visible ancestor tells you a chain exists between you and them, and
          roughly how the part of the organisation you were not given is shaped.
    
          So the report re-roots and says so. Checked before the orphan branch, because that is the branch
          it was wrongly taking.
        */
        if (withheldSupervisor.has(String(p.id))) {
            integrity.withheld++;
            return ROOT_ID;
        }
        if (!byId.has(first)) {
            integrity.orphans++;
            return ROOT_ID;
        }
        integrity.spliced++;
        // Climb to the nearest ancestor the lens can see, or to the root.
        const seen = new Set([String(p.id), first]);
        let cur = supervisorOf(byId.get(first));
        while (cur && !seen.has(cur)) {
            seen.add(cur);
            if (visibleIds.has(cur))
                return personNodeId(cur);
            const mgr = byId.get(cur);
            if (!mgr)
                return ROOT_ID;
            cur = supervisorOf(mgr);
        }
        return ROOT_ID;
    };
    const sorted = [...visible].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    for (const p of sorted) {
        nodes.push({ id: personNodeId(p.id), parentId: parentNodeId(p), kind: 'person', label: p.name, empId: p.id, data: p });
    }
    // Break mutual cycles among visible people so every node reaches the single root.
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    for (const n of nodes) {
        if (n.kind !== 'person')
            continue;
        const seen = new Set([n.id]);
        let parent = n.parentId;
        while (parent && parent !== ROOT_ID) {
            if (seen.has(parent)) {
                n.parentId = ROOT_ID;
                integrity.cycles++;
                break;
            }
            seen.add(parent);
            parent = nodeById.get(parent)?.parentId ?? ROOT_ID;
        }
    }
    // Subtree rollups for manager cards, accumulated post-order and iteratively so a deep chain
    // cannot blow the stack.
    const childrenOf = new Map();
    const allById = new Map();
    for (const n of nodes) {
        allById.set(n.id, n);
        const siblings = childrenOf.get(n.parentId);
        if (siblings)
            siblings.push(n);
        else
            childrenOf.set(n.parentId, [n]);
    }
    const subtree = new Map();
    const order = [];
    const stack = [ROOT_ID];
    while (stack.length) {
        const id = stack.pop();
        order.push(id);
        for (const c of childrenOf.get(id) ?? [])
            stack.push(c.id);
    }
    for (let i = order.length - 1; i >= 0; i--) {
        const node = allById.get(order[i]);
        if (!node || node.kind !== 'person')
            continue;
        const p = node.data;
        const noHc = p.isLeaver || !countsInHeadcount(p);
        const roll = {
            reports: 0,
            salary: noHc
                ? 0
                : (Number(getEmpField(p, 'salary', lens.perspective, design.scenarioOverrides)) || 0) *
                    fxFactor(p, state.assumptions),
            fte: noHc ? 0 : Number(getEmpField(p, 'fte', lens.perspective, design.scenarioOverrides)) || 0,
            composition: emptyComposition(),
        };
        const bucket = currentBucket(design, p.id);
        if (p.isLeaver) {
            /* counts in no segment, matching every other aggregate */
        }
        else if (p.openPosition) {
            roll.composition.Open++;
        }
        else if (p.inactive) {
            roll.composition.Inactive++;
        }
        else if (p.isBuildUp) {
            if (bucket === 'Carve-out')
                roll.composition.CarveBu++;
            else
                roll.composition.RemainBu++;
        }
        else {
            roll.composition[bucket] = (roll.composition[bucket] ?? 0) + 1;
        }
        for (const child of childrenOf.get(node.id) ?? []) {
            const childRoll = subtree.get(child.id);
            if (!childRoll)
                continue;
            // Filled reports only: a vacancy, an inactive person or a leaver counts 0 for itself, but its
            // own subtree still passes through — otherwise a team under a vacant manager would vanish
            // from the count.
            const childNoHc = child.kind === 'person' && !countsInAggregates(child.data);
            roll.reports += (childNoHc ? 0 : 1) + childRoll.reports;
            roll.salary += childRoll.salary;
            roll.fte += childRoll.fte;
            for (const key of Object.keys(roll.composition)) {
                roll.composition[key] += childRoll.composition[key];
            }
        }
        subtree.set(node.id, roll);
        // Only somebody with filled reports is a manager worth showing a rollup for.
        if (roll.reports > 0)
            node._roll = roll;
    }
    // Default expansion opens the root's direct reports and their children, so a deep chain shows its
    // top two levels rather than a single box. Set explicitly on every person, not just the expanded
    // ones, so the flag is never left undefined.
    const expandTo = new Set();
    for (const child of childrenOf.get(ROOT_ID) ?? []) {
        expandTo.add(child.id);
        for (const grandchild of childrenOf.get(child.id) ?? [])
            expandTo.add(grandchild.id);
    }
    for (const n of nodes) {
        if (n.kind === 'person')
            n._expanded = expandTo.has(n.id);
    }
    // Hung off the root, because the root is the only node every reader already has in hand.
    const rootNode = nodes.find((n) => n.kind === 'root');
    if (rootNode && rootNode.kind === 'root')
        rootNode.data.reportingIntegrity = integrity;
    return nodes;
}
/** The population the current perspective considers in scope. */
export function getActivePeople(state, lens) {
    const all = [...state.employees, ...state.design.buildUps];
    if (lens.perspective === 'as-is')
        return all;
    if (lens.viewMode === 'grey')
        return all;
    return all.filter((p) => (currentBucket(state.design, p.id) === PERSPECTIVE_BUCKET[lens.perspective] &&
        stagePassesLens(state.design, p.id, lens)) ||
        // Undecided existing people stay in the Remain view: Remain is the default, not a decision.
        (lens.perspective === 'remain' && !p.isBuildUp && state.design.buckets[p.id] === undefined));
}
export { bucketMetaFor, currentBucket, waveOf };
//# sourceMappingURL=hierarchy.js.map